import type {
  FaucetBankInfo,
  FaucetTransfer,
  FaucetTransferRecord,
  OfficialVaultRequest,
  OfficialVaultRequestInput,
  OfficialVaultStatus,
} from 'src/pages/admin/services/officialVaultService';
import type { VaultParams } from 'src/pages/admin/services/vaultParamsService';
import type { VaultState } from 'src/pages/admin/services/vaultService';
import {
  ApiErrorInfo,
  decimalPlaces,
  decimalToPercent,
  isNonNegativeDecimalString,
  percentToDecimal,
} from 'src/pages/admin/utils/decimal-input';

import { FieldUnits, formatFixedDecimals, formatValue } from '../params/helpers';
import { formatUsdcAmount } from '../vaults/helpers';
import {
  APPROVE_STATUSES,
  FAUCET_ERROR,
  FAUCET_TESTNET_NETWORK,
  FAUCET_VAULT_STATES,
  FEE_PERCENT_MAX_DECIMALS,
  GROUP_CHAT_ID_MAX,
  NAME_MAX,
  NAME_MIN,
  DisplayStatus,
  OFFICIAL_VAULT_ERROR,
  OFFICIAL_VAULT_STATUSES,
  OFFICIAL_VAULTS_NS as NS,
  PRE_REVIEW_STATUSES,
  REJECT_REASON_MAX,
  REJECT_STATUSES,
  REDRIVE_ERROR,
  REVIEW_ERROR,
  STAKE_MAX_DECIMALS,
  STRATEGY_MAX,
  STRATEGY_MIN,
  VAULT_ID_MAX,
  MANAGER_ADDRESS_PATTERN,
  MANAGER_UID_PATTERN,
  VAULT_ID_PATTERN,
  VAULT_ID_PREFIX,
  WIRE_DECIMALS,
  X_HANDLE_MAX,
} from './constants';
import { stakeOwed } from './stakeAmounts';

const EMPTY = '—';

/**
 * A request as this page shows it: the C-08 record plus its catalog row's state, when the operator can read the
 * vault list. The reconcile writes OPEN / CANCELLED a pass after the vault moves (XBIT-4254), and a vault opened for
 * raising before Option B never gets OPEN; the row's state is what says it is live.
 */
export type ApplicationView = OfficialVaultRequest & {
  vaultState?: VaultState | null;
  /** The catalog row has a raise start: a CLOSED vault that raised, as against one closed by a rejection. */
  vaultRaised?: boolean;
};

/** The catalog row's state and whether it ever raised, by vault id. */
export interface CatalogRowState {
  state: VaultState;
  raised: boolean;
}

/** `vaultState` per request, from GET /vault/vaults; a vault not in the catalog yet carries none. */
export const withVaultStates = (
  requests: OfficialVaultRequest[],
  rows: ReadonlyMap<string, CatalogRowState>
): ApplicationView[] =>
  requests.map((r) => {
    const row = rows.get(r.vaultId);
    return { ...r, vaultState: row?.state ?? null, vaultRaised: row?.raised ?? false };
  });

/** The catalog rows the list already carries, by vault id: what a read that cannot reach the catalog keeps. */
export const catalogRowsOf = (views: readonly ApplicationView[]): Map<string, CatalogRowState> =>
  new Map(
    views.flatMap((v) =>
      v.vaultState
        ? [[v.vaultId, { state: v.vaultState, raised: Boolean(v.vaultRaised) }] as const]
        : []
    )
  );

/** The catalog row has left DRAFT (approved, closed, or opened for raising before Option B): no stake is parked. */
const pastDraft = (r: Pick<ApplicationView, 'vaultState'>): boolean =>
  Boolean(r.vaultState) && r.vaultState !== 'DRAFT';

/**
 * The catalog row says the vault went live, whatever C-08 says yet: raising, active or paused, or closed after it
 * raised. A row CLOSED without a raise start is a rejection (D-30: reject, or the stake window, closes the DRAFT).
 */
const rowLive = (r: Pick<ApplicationView, 'vaultState' | 'vaultRaised'>): boolean =>
  r.vaultState === 'RAISING' ||
  r.vaultState === 'ACTIVE' ||
  r.vaultState === 'PAUSED' ||
  (r.vaultState === 'CLOSED' && Boolean(r.vaultRaised));

/** A row closed while still DRAFT: rejected, even before the consumer writes CANCELLED. */
const rowRejected = (r: Pick<ApplicationView, 'vaultState' | 'vaultRaised'>): boolean =>
  r.vaultState === 'CLOSED' && !r.vaultRaised;

/** The `vault.params.core` values a new official vault is checked against (design §3). */
export interface RequestBounds {
  lockupMinDays: number;
  lockupMaxDays: number;
  performanceFeeCap: string;
  ownershipFloor: string;
  /** The stake floor (C-08 v2): a stake below it could not subscribe once the vault opens. */
  minSubscription: string;
}

/** Stored core params, else the engine defaults — the engine's own rule. Null when neither loaded. */
export const boundsFrom = (params: VaultParams | null): RequestBounds | null => {
  const core = params?.core.value ?? params?.coreDefaults ?? null;
  if (!core) return null;
  const { lockupMinDays, lockupMaxDays, performanceFeeCap, ownershipFloor, minSubscription } = core;
  return { lockupMinDays, lockupMaxDays, performanceFeeCap, ownershipFloor, minSubscription };
};

/** Form state: every input as typed. The fee is a PERCENT here and a fraction on the wire. */
export interface RequestDraft {
  vaultId: string;
  name: string;
  strategy: string;
  feePercent: string;
  lockupDays: string;
  xHandle: string;
  groupChatId: string;
  /** The name the app shows for the manager (C-08 v3, XBIT-4367), as typed. */
  managerName: string;
  /** The manager's UID (holder id), as typed. */
  managerHolderId: string;
  /** The manager's HyperCore address, bound to that UID, as typed. */
  managerAddress: string;
  /** USDC, as typed. */
  stakeAmount: string;
}
export type RequestDraftKey = keyof RequestDraft;
export type FieldErrors = Partial<Record<RequestDraftKey, string>>;

export const EMPTY_DRAFT: RequestDraft = {
  vaultId: '',
  name: '',
  strategy: '',
  feePercent: '',
  lockupDays: '',
  xHandle: '',
  groupChatId: '',
  managerName: '',
  managerHolderId: '',
  managerAddress: '',
  stakeAmount: '',
};

/** Draft key → i18n label. The fee's label is "Profit-share" (Module PRD), its key feeRate. */
export const FIELD_LABELS: Record<RequestDraftKey | 'ownershipFloor' | 'sizeCap', string> = {
  vaultId: `${NS}.fields.vaultId`,
  name: `${NS}.fields.name`,
  strategy: `${NS}.fields.strategy`,
  feePercent: `${NS}.fields.feeRate`,
  lockupDays: `${NS}.fields.lockupDays`,
  xHandle: `${NS}.fields.xHandle`,
  groupChatId: `${NS}.fields.groupChatId`,
  managerName: `${NS}.fields.managerName`,
  managerHolderId: `${NS}.fields.managerHolderId`,
  managerAddress: `${NS}.fields.managerAddress`,
  ownershipFloor: `${NS}.fields.ownershipFloor`,
  stakeAmount: `${NS}.fields.stakeAmount`,
  sizeCap: `${NS}.fields.sizeCap`,
};

/** `12.5` at scale 3 → 12500n: the decimal string as an integer count of 10^-scale, exactly. */
const scaled = (value: string, scale: number): bigint => {
  const [whole, fraction = ''] = value.trim().split('.');
  return BigInt(whole + fraction.padEnd(scale, '0'));
};

/**
 * `a < b` on two non-negative decimal strings, exactly — never through Number(), which cannot tell
 * 1000000000000 from 1000000000000.000001 (a money check, convention 12). Either side unusable → false.
 */
export const decimalLessThan = (a: string, b: string): boolean => {
  if (!isNonNegativeDecimalString(a) || !isNonNegativeDecimalString(b)) return false;
  const scale = Math.max(decimalPlaces(a.trim()), decimalPlaces(b.trim()));
  return scaled(a, scale) < scaled(b, scale);
};

/** Application statuses at which a received stake is still parked for the raise (D-30 clause 4). */
const STAKE_PARKED_STATUSES: readonly string[] = [
  'REQUESTED',
  'ACCOUNT_READY',
  'CATALOGUED',
  'ACTIVATED',
  'PENDING_REVIEW',
];

/**
 * The i18n key for the stake's transfer status in the details drawer; null without one. The engine's PENDING is any
 * attributed transfer not yet decided, so it reads "Received, awaiting approval" (PO, 2026-10-01; Option B wording) only
 * while the approval is next: PENDING_REVIEW with the catalog row still DRAFT. Before that (a system step, SYSTEM_STEPS)
 * it reads "Received, verifying": the reconcile moves CATALOGUED → ACTIVATED → PENDING_REVIEW one pass at a time, so
 * the stake shows received before Approve opens, and the list polls until it does (XBIT-4403). C-08
 * lags the vault (the reconcile writes OPEN / CANCELLED a pass later, XBIT-4254), so a vault already live, or CLOSED by a rejection,
 * settles it the same way displayStatus does (rowLive / rowRejected). Otherwise a still-PENDING transfer is waiting for the runner to
 * subscribe or refund it, and reads "Received, not yet subscribed".
 */
export const stakeTransferKey = (
  r: Pick<ApplicationView, 'status' | 'vaultState' | 'stake'>,
  transferStatus: string | null | undefined
): string | null => {
  // XBIT-4470: no transfer yet while the stake is owed reads "No stake received yet", not "—".
  if (!transferStatus) return stakeOwed(r) ? `${NS}.details.transfer.NOT_RECEIVED` : null;
  const parked = STAKE_PARKED_STATUSES.includes(r.status) && !pastDraft(r);
  if (transferStatus === 'PENDING' && !parked) return `${NS}.details.transfer.PENDING_UNSETTLED`;
  if (transferStatus === 'PENDING' && SYSTEM_STEPS.includes(r.status)) {
    return `${NS}.details.transfer.PENDING_VERIFYING`;
  }
  return `${NS}.details.transfer.${transferStatus}`;
};

/** A USDC amount with its unit, grouped (PO, 2026-10-01): "1000" → "1,000 USDC". */
export const usdcText = (raw: string, units?: FieldUnits): string =>
  raw === '' ? EMPTY : `${formatUsdcAmount(raw)} ${units?.usdc ?? 'USDC'}`;

/**
 * The vault's size cap the stake buys: stake ÷ ownership floor (PRD §1.2, `ComputeSizeCap`), to 2 dp, rounded
 * DOWN like every figure (PRD §6.6). Computed on the decimal strings as integers, never in binary floating point,
 * where `150.5 / 0.05` is `3009.99…`. Display only — the engine computes the real cap from the manager's present
 * value. Null when either input is unusable.
 */
export const sizeCapFor = (stake: string, ownershipFloor: string): string | null => {
  if (!isNonNegativeDecimalString(stake) || !isNonNegativeDecimalString(ownershipFloor))
    return null;
  const scale = Math.max(decimalPlaces(stake.trim()), decimalPlaces(ownershipFloor.trim()));
  const floor = scaled(ownershipFloor, scale);
  if (floor <= BigInt(0)) return null;
  const cents = (scaled(stake, scale) * BigInt(100)) / floor; // BigInt division truncates: round down
  const hundred = BigInt(100);
  return `${cents / hundred}.${(cents % hundred).toString().padStart(2, '0')}`;
};

/** Code points, as the engine counts runes: `😀` is one character, not two. */
const chars = (value: string): number => Array.from(value).length;

/** `Crème Brûlée #1` → `creme-brulee-1`. Letters outside a–z (after stripping accents) are dropped. */
export const slugify = (name: string): string =>
  name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** djb2 over the trimmed name's UTF-16 units, mod 2^32, as 8 hex: the same name always gives the same ID. */
const nameHash = (name: string): string => {
  const text = name.trim();
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = (h * 33 + text.charCodeAt(i)) % 4294967296;
  return h.toString(16).padStart(8, '0');
};

/**
 * The vault ID, generated from the name and never typed (PO, 2026-10-01): `official-<slug>` within the catalog id
 * rule. A name with no Latin letters or digits (`金库一号`, which PRD §9 allows) gets `official-<8 hex>` from a hash
 * of the name instead, so it can still be created. '' only for a blank name.
 */
export const suggestVaultId = (name: string): string => {
  if (!name.trim()) return '';
  const slug = slugify(name) || nameHash(name);
  return `${VAULT_ID_PREFIX}${slug}`.slice(0, VAULT_ID_MAX).replace(/-+$/, '');
};

/**
 * `id`, or `id-2`, `id-3`, … — the first one no listed request holds (XBIT-4321). A FAILED request keeps its vault
 * ID for good (C-08), so resubmitting its terms needs a fresh one; the name, released by the failure, stays.
 */
export const freeVaultId = (id: string, taken: ReadonlySet<string>): string => {
  if (!id || !taken.has(id)) return id;
  for (let n = 2; ; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${id.slice(0, VAULT_ID_MAX - suffix.length).replace(/-+$/, '')}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
};

/** "150.000000" → "150", "0.5" → "0.5": a wire decimal as an operator would type it. */
const asTyped = (value: string): string =>
  value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;

/**
 * The create form prefilled from a FAILED request (XBIT-4321, Retry): its terms as typed, the fee back as a percent,
 * the vault ID regenerated from the name and moved off every ID already taken.
 */
export const draftFromRequest = (
  r: OfficialVaultRequest,
  taken: ReadonlySet<string>
): RequestDraft => ({
  name: r.name,
  vaultId: freeVaultId(suggestVaultId(r.name), taken),
  strategy: r.strategy,
  feePercent: asTyped(decimalToPercent(r.feeRate) ?? ''),
  lockupDays: String(r.lockupDays),
  xHandle: r.xHandle ?? '',
  groupChatId: r.groupChatId ?? '',
  managerName: r.manager?.name ?? '',
  managerHolderId: r.manager?.holderId ?? '',
  managerAddress: r.manager?.address ?? '',
  stakeAmount: r.stake ? asTyped(r.stake.expected) : '',
});

/**
 * Retry is offered on a FAILED official request only (XBIT-4321). The form it opens is Create official vault, which
 * sends no kind (OFFICIAL by default), so retrying a community application there would resubmit it as an official
 * vault; it waits for a community intake (XBIT-4253).
 */
export const canRetry = (r: Pick<OfficialVaultRequest, 'status' | 'kind'>): boolean =>
  r.status === 'FAILED' && r.kind !== 'COMMUNITY';

const lengthError = (value: string, max: number, required: boolean): string | undefined => {
  if (!value) return required ? `${NS}.errors.required` : undefined;
  return chars(value) > max ? `${NS}.errors.tooLong` : undefined;
};

/** Design §3, mirrored. Number() is used for ranges only — never to build the payload. */
export const validateRequestDraft = (d: RequestDraft, bounds: RequestBounds): FieldErrors => {
  const errors: FieldErrors = {};

  const vaultId = d.vaultId.trim();
  if (!vaultId) errors.vaultId = `${NS}.errors.vaultIdFromName`;
  else if (!VAULT_ID_PATTERN.test(vaultId)) errors.vaultId = `${NS}.errors.vaultIdFormat`;

  const name = d.name.trim();
  if (!name) errors.name = `${NS}.errors.required`;
  else if (chars(name) < NAME_MIN || chars(name) > NAME_MAX)
    errors.name = `${NS}.errors.nameLength`;

  const strategy = d.strategy.trim();
  if (!strategy) errors.strategy = `${NS}.errors.required`;
  else if (chars(strategy) < STRATEGY_MIN || chars(strategy) > STRATEGY_MAX)
    errors.strategy = `${NS}.errors.strategyLength`;

  const fee = d.feePercent.trim();
  if (!fee) errors.feePercent = `${NS}.errors.required`;
  else if (!isNonNegativeDecimalString(fee)) errors.feePercent = `${NS}.errors.notANumber`;
  else if (decimalPlaces(fee) > FEE_PERCENT_MAX_DECIMALS) {
    errors.feePercent = `${NS}.errors.tooManyDecimals`;
  } else {
    const rate = Number(percentToDecimal(fee));
    if (rate > Number(bounds.performanceFeeCap) || rate >= 1) {
      errors.feePercent = `${NS}.errors.feeAboveCap`;
    }
  }

  const lockup = d.lockupDays.trim();
  if (!lockup) errors.lockupDays = `${NS}.errors.required`;
  else if (!/^\d+$/.test(lockup)) errors.lockupDays = `${NS}.errors.notAWholeNumber`;
  else {
    const days = Number.parseInt(lockup, 10);
    if (days < bounds.lockupMinDays || days > bounds.lockupMaxDays) {
      errors.lockupDays = `${NS}.errors.lockupOutOfRange`;
    }
  }

  // C-08 v3 (XBIT-4367): the vault name's bounds, because the app shows the two side by side.
  const managerName = d.managerName.trim();
  if (!managerName) errors.managerName = `${NS}.errors.required`;
  else if (chars(managerName) < NAME_MIN || chars(managerName) > NAME_MAX)
    errors.managerName = `${NS}.errors.nameLength`;

  const holderId = d.managerHolderId.trim().toLowerCase(); // ops paste upper-case UIDs; the wire is lower-case
  if (!holderId) errors.managerHolderId = `${NS}.errors.required`;
  else if (!MANAGER_UID_PATTERN.test(holderId)) errors.managerHolderId = `${NS}.errors.uidFormat`;

  const address = d.managerAddress.trim();
  if (!address) errors.managerAddress = `${NS}.errors.required`;
  else if (!MANAGER_ADDRESS_PATTERN.test(address))
    errors.managerAddress = `${NS}.errors.addressFormat`;

  const stake = d.stakeAmount.trim();
  if (!stake) errors.stakeAmount = `${NS}.errors.required`;
  else if (!isNonNegativeDecimalString(stake)) errors.stakeAmount = `${NS}.errors.notANumber`;
  else if (decimalPlaces(stake) > STAKE_MAX_DECIMALS)
    errors.stakeAmount = `${NS}.errors.tooManyStakeDecimals`;
  else if (decimalLessThan(stake, bounds.minSubscription))
    errors.stakeAmount = `${NS}.errors.stakeBelowMin`;

  const xHandle = lengthError(d.xHandle.trim(), X_HANDLE_MAX, false);
  if (xHandle) errors.xHandle = xHandle;
  const groupChatId = lengthError(d.groupChatId.trim(), GROUP_CHAT_ID_MAX, false);
  if (groupChatId) errors.groupChatId = groupChatId;

  return errors;
};

/** The POST body. Call only on a draft `validateRequestDraft` accepted. */
export const toRequestInput = (d: RequestDraft): OfficialVaultRequestInput => {
  const xHandle = d.xHandle.trim();
  const groupChatId = d.groupChatId.trim();
  return {
    vaultId: d.vaultId.trim(),
    name: d.name.trim(),
    strategy: d.strategy.trim(),
    feeRate: formatFixedDecimals(percentToDecimal(d.feePercent.trim()) ?? '', WIRE_DECIMALS),
    lockupDays: Number.parseInt(d.lockupDays.trim(), 10),
    ...(xHandle ? { xHandle } : {}),
    ...(groupChatId ? { groupChatId } : {}),
    manager: {
      address: d.managerAddress.trim(),
      holderId: d.managerHolderId.trim().toLowerCase(),
      name: d.managerName.trim(),
    },
    stakeAmount: formatFixedDecimals(d.stakeAmount.trim(), WIRE_DECIMALS),
  };
};

export interface SummaryRow {
  key: string;
  /** i18n key */
  label: string;
  value: string;
}

/** Every value the request will carry, in form order, including the floor recorded from the parameters. */
export const requestSummary = (
  input: OfficialVaultRequestInput,
  bounds: RequestBounds,
  units?: FieldUnits
): SummaryRow[] => [
  { key: 'name', label: FIELD_LABELS.name, value: input.name },
  { key: 'vaultId', label: FIELD_LABELS.vaultId, value: input.vaultId },
  { key: 'managerName', label: FIELD_LABELS.managerName, value: input.manager.name },
  { key: 'managerHolderId', label: FIELD_LABELS.managerHolderId, value: input.manager.holderId },
  { key: 'managerAddress', label: FIELD_LABELS.managerAddress, value: input.manager.address },
  { key: 'strategy', label: FIELD_LABELS.strategy, value: input.strategy },
  {
    key: 'stakeAmount',
    label: FIELD_LABELS.stakeAmount,
    value: usdcText(input.stakeAmount, units),
  },
  {
    key: 'feeRate',
    label: FIELD_LABELS.feePercent,
    value: formatValue('percent', input.feeRate, units),
  },
  {
    key: 'lockupDays',
    label: FIELD_LABELS.lockupDays,
    value: formatValue('days', input.lockupDays, units),
  },
  { key: 'xHandle', label: FIELD_LABELS.xHandle, value: input.xHandle ?? EMPTY },
  { key: 'groupChatId', label: FIELD_LABELS.groupChatId, value: input.groupChatId ?? EMPTY },
  {
    key: 'ownershipFloor',
    label: FIELD_LABELS.ownershipFloor,
    value: formatValue('percent', bounds.ownershipFloor, units),
  },
  {
    key: 'sizeCap',
    label: FIELD_LABELS.sizeCap,
    value: (() => {
      const cap = sizeCapFor(input.stakeAmount, bounds.ownershipFloor);
      return cap === null ? EMPTY : usdcText(cap, units);
    })(),
  },
];

/** Code → i18n key. Upstream text is never shown for these: English, and may name internals. */
const FAILURE_KEYS: Record<string, string> = {
  [OFFICIAL_VAULT_ERROR.NOT_PUBLISHED]: `${NS}.errors.notPublished`,
  [OFFICIAL_VAULT_ERROR.ENGINE_UNAVAILABLE]: `${NS}.errors.engineUnavailable`,
  [OFFICIAL_VAULT_ERROR.ENGINE_AUTH_FAILED]: `${NS}.errors.engineAuthFailed`,
  [OFFICIAL_VAULT_ERROR.ENGINE_TIMEOUT]: `${NS}.errors.engineTimeout`,
  [OFFICIAL_VAULT_ERROR.ENGINE_NOT_CONFIGURED]: `${NS}.errors.engineNotConfigured`,
};

/** Every code the page translates (the i18n test resolves each key). */
export const SUBMIT_ERROR_KEYS: Record<string, string> = {
  ...FAILURE_KEYS,
  [OFFICIAL_VAULT_ERROR.INVALID]: `${NS}.errors.invalid`,
  [OFFICIAL_VAULT_ERROR.CONFLICT]: `${NS}.errors.conflict`,
};

/** The field a 409 names, when it names one: `name` for a vault name already taken (case-insensitive). */
export const conflictField = (error: unknown): string | undefined => {
  const body = (error as { body?: { code?: unknown; field?: unknown } } | null)?.body;
  return body?.code === OFFICIAL_VAULT_ERROR.CONFLICT && typeof body.field === 'string'
    ? body.field
    : undefined;
};

/**
 * The request may exist in the engine even though the call failed. After NOT_PUBLISHED, resubmitting the same
 * values sends it on; after a timeout, resubmitting them (once the list shows it as Requested) records it —
 * the idempotent repeat also writes the audit row the timed-out call may lack (XBIT-3859).
 */
export const isMaybeRecorded = (info: ApiErrorInfo): boolean =>
  info.code === OFFICIAL_VAULT_ERROR.NOT_PUBLISHED ||
  info.code === OFFICIAL_VAULT_ERROR.ENGINE_TIMEOUT ||
  info.status === 504;

export const submitErrorMessages = (
  info: ApiErrorInfo,
  translate: (key: string) => string
): string[] => {
  const failureKey = info.code ? FAILURE_KEYS[info.code] : undefined;
  if (failureKey) return [translate(failureKey)];
  // A 504 without the BFF code (a gateway in front of it timed out) has the same unknown outcome.
  if (info.status === 504) return [translate(`${NS}.errors.engineTimeout`)];
  // A conflict has three causes (id taken, other terms, already past REQUESTED); the engine's
  // reason says which one.
  if (info.code === OFFICIAL_VAULT_ERROR.CONFLICT) {
    return [translate(`${NS}.errors.conflict`), ...info.messages];
  }
  // A refused request (engine rule, or the BFF's DTO check): the reason names the field.
  if (info.code === OFFICIAL_VAULT_ERROR.INVALID || info.status === 422 || info.status === 400) {
    return [translate(`${NS}.errors.invalid`), ...info.messages];
  }
  return [translate(`${NS}.errors.submitFailed`)];
};

export const listErrorMessage = (info: ApiErrorInfo, translate: (key: string) => string): string =>
  translate((info.code && FAILURE_KEYS[info.code]) || `${NS}.loadFailed`);

export type StatusColor = 'default' | 'info' | 'success' | 'warning' | 'error';

/**
 * C-08 statuses before the vault is live. PENDING_REVIEW is D-30's review gate (XBIT-4251), listed ahead of the engine
 * so the list does not change when it ships.
 */
const IN_FLIGHT = ['REQUESTED', 'ACCOUNT_READY', 'CATALOGUED', 'ACTIVATED', 'PENDING_REVIEW'];

const DISPLAY_COLOR: Record<DisplayStatus, StatusColor> = {
  PENDING: 'warning',
  PROVISIONING: 'info',
  LIVE: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
  FAILED: 'error',
  PROVISIONING_FAILED: 'error',
};

/**
 * An official vault waiting on the operator: before its review (CATALOGUED, ACTIVATED) and at it (PENDING_REVIEW,
 * where Approve opens it). Pending always means "ops has something coming or to do".
 */
const OFFICIAL_PENDING: readonly string[] = [...PRE_REVIEW_STATUSES, 'PENDING_REVIEW'];

/**
 * The Status column's word for a request (PO, 2026-10-01: Pending / Live / Rejected / Cancelled). Community: Pending /
 * Rejected / Cancelled / Live, plus Failed if provisioning stopped. Official: Provisioning / Pending / Provisioning
 * failed / Live. `OPEN` (= the vault ACTIVE, D-30 amendment 2026-10-02), or a catalog row past DRAFT (`vaultState`),
 * is live. C-08 v2 writes CANCELLED both for a review rejection and for the stake window elapsing; the record tells
 * them apart: a rejection carries `review.outcome = REJECTED` (f1 review, 2026-10-02). A record without `kind` is a v1
 * official request.
 */
export const displayStatus = (
  request: Pick<ApplicationView, 'status' | 'kind' | 'vaultState' | 'vaultRaised' | 'review'>
): DisplayStatus | null => {
  const community = request.kind === 'COMMUNITY';
  const status: string = request.status;
  // Terminal C-08 outcomes first: a rejected, cancelled or failed request never reads Live from its row.
  if (status === 'CANCELLED')
    return request.review?.outcome === 'REJECTED' ? 'REJECTED' : 'CANCELLED';
  if (rowRejected(request)) return 'REJECTED';
  if (status === 'FAILED') return community ? 'FAILED' : 'PROVISIONING_FAILED';
  if (status === 'OPEN' || rowLive(request)) return 'LIVE';
  if (!community && OFFICIAL_PENDING.includes(status)) return 'PENDING';
  if (IN_FLIGHT.includes(status)) return community ? 'PENDING' : 'PROVISIONING';
  return null;
};

/** A status added later (C-06 §4 calls that breaking) shows raw, uncoloured — never a crash. */
export const statusChip = (
  request: Pick<ApplicationView, 'status' | 'kind' | 'vaultState' | 'vaultRaised' | 'review'>
): { labelKey: string | null; color: StatusColor } => {
  const shown = displayStatus(request);
  return shown
    ? { labelKey: `${NS}.status.${shown}`, color: DISPLAY_COLOR[shown] }
    : { labelKey: null, color: 'default' };
};

/** The C-08 step's own label, for the details drawer; null for a step this build does not know. */
export const stepLabelKey = (status: string): string | null =>
  (OFFICIAL_VAULT_STATUSES as readonly string[]).includes(status) ? `${NS}.step.${status}` : null;

/** Who created the request: the admin's name when the BFF resolved it, else the id (XBIT-4320). */
export const requesterLabel = (
  r: Pick<OfficialVaultRequest, 'requestedBy' | 'requestedByName'>
): string => r.requestedByName?.trim() || r.requestedBy;

export const sortByRequestedAtDesc = (list: OfficialVaultRequest[]): OfficialVaultRequest[] =>
  [...list].sort((a, b) => (Date.parse(b.requestedAt) || 0) - (Date.parse(a.requestedAt) || 0));

/**
 * What the approve dialog says about the manager's stake (D-30 clause 5: with no manager shares the size cap is 0). The
 * engine re-checks the parked stake on approve; the dialog only warns, it does not block. `unconfirmed` also covers a
 * v1 record and an engine that does not report the received stake yet (XBIT-4251).
 */
export type StakeNotice =
  | { kind: 'unconfirmed' }
  | { kind: 'rejected' }
  | { kind: 'short'; received: string; expected: string };

export const stakeNotice = (
  r: Pick<OfficialVaultRequest, 'stake'> | null | undefined
): StakeNotice | null => {
  const stake = r?.stake;
  if (!stake || !stake.received) return { kind: 'unconfirmed' };
  if (stake.transferStatus === 'REJECTED') return { kind: 'rejected' };
  const { received, expected } = stake;
  if (!isNonNegativeDecimalString(received) || !isNonNegativeDecimalString(expected))
    return { kind: 'unconfirmed' };
  const scale = Math.max(decimalPlaces(received.trim()), decimalPlaces(expected.trim()));
  return scaled(received, scale) < scaled(expected, scale)
    ? { kind: 'short', received, expected }
    : null;
};

// ---------------------------------------------------------------------------------------------------------------------
// D-30 review gate (STORY-22 T-07, XBIT-4257)

/** The catalog row can still be moved by a decision: no row yet, or DRAFT (the engine re-checks it before recording). */
const rowStillDraft = (r: Pick<ApplicationView, 'vaultState'>): boolean =>
  !r.vaultState || r.vaultState === 'DRAFT';

/**
 * An approval that has not taken effect: recorded, but the vault is still DRAFT (the move to ACTIVE did not go through).
 * STORY-22 T-02b (Connor, 2026-10-01): it may be replaced by a rejection once `review.at` is 60 s old; the engine answers
 * a replacement that comes too early with `review.retryAfter`. Approving again replays it.
 */
export const isUnappliedApproval = (r: Pick<ApplicationView, 'review' | 'vaultState'>): boolean =>
  r.review?.outcome === 'APPROVED' && rowStillDraft(r);

/** The engine's quiet period before an unapplied approval may be replaced (T-02b). */
export const REVIEW_QUIET_PERIOD_MS = 60_000;

/** Seconds still to wait before an unapplied approval may be replaced; 0 once the quiet period has passed. */
export const replaceWaitSeconds = (
  r: Pick<ApplicationView, 'review' | 'vaultState'>,
  now: number
): number => {
  if (!isUnappliedApproval(r)) return 0;
  const at = Date.parse(r.review?.at ?? '');
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.ceil((at + REVIEW_QUIET_PERIOD_MS - now) / 1000));
};

/** C-08 statuses the treasury moves on from by itself: provisioning, cataloguing, the venue, the stake. */
const SYSTEM_STEPS: readonly string[] = ['REQUESTED', 'ACCOUNT_READY', 'CATALOGUED', 'ACTIVATED'];

const FINAL_STATUSES: readonly string[] = ['OPEN', 'CANCELLED', 'FAILED'];

/**
 * The record still owes a step the system takes, not an operator (XBIT-4383): the list polls while any row does.
 * - No review: a system step, on a vault with no catalog row yet, still DRAFT, or CLOSED without ever opening (the
 *   stake window's close; the reconcile adopts no other).
 * - APPROVED on ACTIVE (the reconcile writes OPEN next), or on DRAFT inside the quiet period, while the open may still
 *   land; past it, the open failed and an operator retries.
 * - REJECTED on DRAFT (the reconcile closes it, XBIT-4254) or CLOSED (it writes CANCELLED next).
 * PENDING_REVIEW with no decision waits on an operator, a final status never moves, and any other pairing is a
 * C-08 backstop mismatch resolved by hand (runbook 19 §4), so none of them polls.
 */
export const isApplicationMoving = (
  r: Pick<ApplicationView, 'status' | 'review' | 'vaultState' | 'vaultRaised'>,
  now: number
): boolean => {
  if (FINAL_STATUSES.includes(r.status)) return false;
  switch (r.review?.outcome) {
    case 'APPROVED':
      return (
        r.vaultState === 'ACTIVE' || (r.vaultState === 'DRAFT' && replaceWaitSeconds(r, now) > 0)
      );
    case 'REJECTED':
      return r.vaultState === 'DRAFT' || r.vaultState === 'CLOSED';
    default:
      return SYSTEM_STEPS.includes(r.status) && (rowStillDraft(r) || rowRejected(r));
  }
};

/**
 * Approve is open at PENDING_REVIEW (the manager's stake is parked); it is the only way a vault opens (STORY-22 AC15).
 * A recorded APPROVED whose move to ACTIVE did not go through (the vault is still DRAFT) is offered again: approving
 * again replays it (the engine keys the replay on the decision alone, D-30 amendment 2026-10-02).
 */
export const canApprove = (r: Pick<ApplicationView, 'status' | 'review' | 'vaultState'>): boolean =>
  APPROVE_STATUSES.includes(r.status) && r.review?.outcome !== 'REJECTED' && rowStillDraft(r);

/**
 * Reject is open until the review, while the row is DRAFT: a recorded REJECTED is offered again to replay the close,
 * and an unapplied approval may be rejected instead (T-02b). An approval that took effect is never rejected here.
 */
export const canReject = (r: Pick<ApplicationView, 'status' | 'review' | 'vaultState'>): boolean =>
  REJECT_STATUSES.includes(r.status) &&
  (r.review?.outcome !== 'APPROVED' || isUnappliedApproval(r)) &&
  rowStillDraft(r);

/** Statuses at which the application has a vault row and is still going (XBIT-4401). */
const CONTACT_STATUSES: readonly string[] = ['CATALOGUED', 'ACTIVATED', 'PENDING_REVIEW', 'OPEN'];

/**
 * XBIT-4401 (QA; f1's condition 1): the contact edit is offered from CATALOGUED on, while the vault is not CLOSED. A
 * row whose vault state is unknown (the vault list could not be read) still has its vault row; the engine refuses a
 * CLOSED one anyway.
 */
export const canEditApplicationContact = (
  r: Pick<ApplicationView, 'status' | 'vaultState'>
): boolean => CONTACT_STATUSES.includes(r.status) && r.vaultState !== 'CLOSED';

/** The engine's bound, mirrored: 1–500 characters after trimming, counted as code points. */
export const validateRejectReason = (reason: string): string | undefined => {
  const n = Array.from(reason.trim()).length;
  if (n === 0) return `${NS}.errors.required`;
  return n > REJECT_REASON_MAX ? `${NS}.review.errors.reasonTooLong` : undefined;
};

/** The `review` object the BFF puts on a review error (XBIT-4256): whether the decision stands, and why it was refused. */
export interface ReviewErrorDetail {
  recorded?: boolean;
  vaultState?: string;
  /** T-02b: seconds still to wait before an unapplied approval may be replaced. Only a reject can be told this. */
  retryAfter?: number;
}

export const reviewErrorDetail = (error: unknown): ReviewErrorDetail => {
  const review = (error as { body?: { review?: unknown } } | null)?.body?.review;
  return review && typeof review === 'object' ? (review as ReviewErrorDetail) : {};
};

const reviewLead = (info: ApiErrorInfo, detail: ReviewErrorDetail): string | null => {
  if (info.code === REVIEW_ERROR.NOT_PENDING) return `${NS}.review.errors.notPending`;
  if (info.code === REVIEW_ERROR.CONFLICT) {
    if (detail.recorded) return `${NS}.review.errors.recorded`;
    return typeof detail.retryAfter === 'number'
      ? `${NS}.review.errors.tooEarly`
      : `${NS}.review.errors.conflict`;
  }
  if (info.code === REVIEW_ERROR.INVALID || info.status === 400)
    return `${NS}.review.errors.invalid`;
  return null;
};

/**
 * A refused review, in the operator's terms. C-06: `recorded` decides the lead line, never the message: true means the
 * decision stands and only the vault update is outstanding, so the same action again applies it.
 */
export const reviewErrorMessages = (
  info: ApiErrorInfo,
  detail: ReviewErrorDetail,
  translate: (key: string, options?: object) => string
): string[] => {
  const failureKey = info.code ? FAILURE_KEYS[info.code] : undefined;
  if (failureKey) return [translate(failureKey)];
  if (info.status === 504) return [translate(`${NS}.errors.engineTimeout`)];
  const lead = reviewLead(info, detail);
  if (!lead) return [translate(`${NS}.review.errors.generic`)];
  // Too early: the engine's seconds, not its sentence, so the dialog's countdown and the lead agree.
  if (lead.endsWith('.tooEarly')) return [translate(lead, { seconds: detail.retryAfter })];
  return [translate(lead), ...info.messages];
};

// ---------------------------------------------------------------------------------------------------------------------
// XBIT-4240 re-drive of a FAILED official request

/**
 * "Resume from failed step" is offered on a FAILED official request that says where it failed (XBIT-4240, C-08 v3.1).
 * It is not the existing Retry, which resubmits the terms as a NEW request under a fresh id (canRetry): the re-drive keeps the
 * id and the terms, so it helps when the cause was outside the terms. The engine decides the rest (a step it does not know
 * is refused by name). A community request waits for the app's own flow.
 */
export const canRedrive = (r: Pick<OfficialVaultRequest, 'status' | 'kind' | 'failure'>): boolean =>
  r.status === 'FAILED' && r.kind !== 'COMMUNITY' && Boolean(r.failure);

/** Code → i18n key of the re-drive's own refusals; each is a 409 that wrote nothing. */
export const REDRIVE_ERROR_KEYS: Record<string, string> = {
  [REDRIVE_ERROR.NOT_FAILED]: `${NS}.redrive.errors.notFailed`,
  [REDRIVE_ERROR.NOT_OFFICIAL]: `${NS}.redrive.errors.notOfficial`,
  [REDRIVE_ERROR.STEP_UNKNOWN]: `${NS}.redrive.errors.stepUnknown`,
  [REDRIVE_ERROR.CONFLICT]: `${NS}.redrive.errors.conflict`,
  [REDRIVE_ERROR.NAME_TAKEN]: `${NS}.redrive.errors.nameTaken`,
};

/**
 * A refused re-drive, in the console's words and then the BFF's own text (the engine's reason for it), so the operator
 * reads exactly what was refused. Nothing was written in any of these; a timeout leaves the outcome unknown, so the
 * dialog says to reload the list.
 */
export const redriveErrorMessages = (
  info: ApiErrorInfo,
  translate: (key: string, options?: object) => string
): string[] => {
  const failureKey = info.code ? FAILURE_KEYS[info.code] : undefined;
  if (failureKey) return [translate(failureKey)];
  if (info.status === 504) return [translate(`${NS}.redrive.errors.timeout`)];
  const lead = info.code ? REDRIVE_ERROR_KEYS[info.code] : undefined;
  if (lead) return [translate(lead), ...info.messages];
  if (info.status === 403) return [translate(`${NS}.redrive.errors.forbidden`)];
  return [translate(`${NS}.redrive.errors.generic`)];
};

// ---------------------------------------------------------------------------------------------------------------------
// XBIT-4348 testnet faucet

/**
 * The bank account the faucet sends from, as the screen may use it. The hidden arm carries no address, so a
 * component holding an offered bank always has one to read a balance with (XBIT-4348).
 */
export type FaucetBank =
  | { offered: false }
  | {
      offered: true;
      address: string;
      network: typeof FAUCET_TESTNET_NETWORK;
      maxAmount: string | null;
    };

/** A bank a component may render: the offered arm alone, so an address is never absent. */
export type OfferedFaucetBank = Extract<FaucetBank, { offered: true }>;

/** Guard clauses, in order: a non-Testnet or absent network hides the action; so does an address that is not an address. */
export const faucetBank = (info: FaucetBankInfo | null | undefined): FaucetBank => {
  if (info?.network !== FAUCET_TESTNET_NETWORK) return { offered: false };
  const address = info.address?.trim() ?? '';
  // MANAGER_ADDRESS_PATTERN is the HyperCore address shape; the bank's is the same one.
  if (!MANAGER_ADDRESS_PATTERN.test(address)) return { offered: false };
  // XBIT-4400: the per-transfer ceiling, so the suggested amount can respect it; null from an older BFF.
  return {
    offered: true,
    address,
    network: FAUCET_TESTNET_NETWORK,
    maxAmount: info.maxAmount?.trim() || null,
  };
};

export interface FaucetDraft {
  amount: string;
  userId: string;
}
export const EMPTY_FAUCET_DRAFT: FaucetDraft = { amount: '', userId: '' };
export type FaucetFieldErrors = Partial<Record<keyof FaucetDraft, string>>;

/**
 * The engine is the authority on the amount ceiling and on the user binding; this only saves a round trip on the
 * two rules the browser can see. No decimal-place cap: the venue and the ledger path own precision (decision 6).
 */
export const validateFaucetDraft = (draft: FaucetDraft): FaucetFieldErrors => {
  const errors: FaucetFieldErrors = {};
  const amount = draft.amount.trim();
  if (!amount) errors.amount = `${NS}.errors.required`;
  else if (!isNonNegativeDecimalString(amount)) errors.amount = `${NS}.errors.notANumber`;
  else if (Number(amount) <= 0) errors.amount = `${NS}.faucet.errors.amountNotPositive`;

  const userId = draft.userId.trim().toLowerCase(); // ops paste upper-case UIDs; the wire is lower-case
  if (!userId) errors.userId = `${NS}.errors.required`;
  else if (!MANAGER_UID_PATTERN.test(userId)) errors.userId = `${NS}.errors.uidFormat`;
  return errors;
};

/** The POST body. Call only on a draft `validateFaucetDraft` accepted. */
export const toFaucetInput = (vaultId: string, draft: FaucetDraft): FaucetTransfer => ({
  vaultId,
  userId: draft.userId.trim().toLowerCase(),
  amount: draft.amount.trim(),
});

/** One intended transfer and the key minted for it, held together so the key cannot drift from the payload it covers. */
export interface FaucetAttempt {
  key: string;
  transfer: FaucetTransfer;
}

const sameTransfer = (a: FaucetTransfer, b: FaucetTransfer): boolean =>
  a.vaultId === b.vaultId && a.userId === b.userId && a.amount === b.amount;

/**
 * The attempt to send this transfer under. A retry of the same payload reuses its key, so a double-click or an
 * HTTP retry is one transfer; an edited draft is a different transfer and earns a new key, because reusing one
 * there would have the engine report a replay and never send the corrected amount. Compared on the mapped
 * payload, so retyped whitespace or UID casing is not a different transfer.
 */
export const faucetAttemptFor = (
  previous: FaucetAttempt | null,
  transfer: FaucetTransfer,
  mintKey: () => string
): FaucetAttempt =>
  previous && sameTransfer(previous.transfer, transfer) ? previous : { key: mintKey(), transfer };

/** `crypto.randomUUID` is available in every browser this admin targets. */
export const newIdempotencyKey = (): string => crypto.randomUUID();

/**
 * Code → lead-in. Keyed on the BFF's own coarse codes, which are the contract the browser is given; the
 * engine's specific `Faucet_*` is reported verbatim in the code line rather than mapped, so this page does not
 * track a sibling repo's evolving code list.
 */
export const FAUCET_ERROR_KEYS: Record<string, string> = {
  [FAUCET_ERROR.NOT_ENABLED]: `${NS}.faucet.errors.notEnabled`,
  [FAUCET_ERROR.VAULT_NOT_FOUND]: `${NS}.faucet.errors.vaultNotFound`,
  [FAUCET_ERROR.INVALID]: `${NS}.faucet.errors.invalid`,
  [FAUCET_ERROR.CONFLICT]: `${NS}.faucet.errors.conflict`,
  [FAUCET_ERROR.UPSTREAM]: `${NS}.faucet.errors.upstream`,
  [FAUCET_ERROR.ENGINE_UNAVAILABLE]: `${NS}.errors.engineUnavailable`,
  [FAUCET_ERROR.ENGINE_AUTH_FAILED]: `${NS}.errors.engineAuthFailed`,
};

/** The most specific code the BFF returned: the engine's `upstreamCode` when there is one, else the BFF's own. */
export const faucetErrorCode = (error: unknown): string | undefined => {
  const body = (error as { body?: { code?: unknown; upstreamCode?: unknown } } | null)?.body;
  return [body?.upstreamCode, body?.code].find((code): code is string => typeof code === 'string');
};

/** The lead-in comes from whichever of the two codes the registry knows; the specific one is always shown. */
export const faucetErrorMessages = (
  info: ApiErrorInfo,
  code: string | undefined,
  translate: (key: string, options?: object) => string
): string[] => {
  const codeLine = code ? [translate(`${NS}.faucet.errors.code`, { code })] : [];
  const leadKey = (code && FAUCET_ERROR_KEYS[code]) || (info.code && FAUCET_ERROR_KEYS[info.code]);
  if (leadKey) return [translate(leadKey), ...info.messages, ...codeLine];
  return [translate(`${NS}.faucet.errors.generic`), ...codeLine];
};

/**
 * The engine takes a faucet transfer for a vault of either kind (XBIT-4469) whose catalog row is DRAFT, RAISING or
 * ACTIVE; PAUSED and CLOSED are refused, so the action is not offered. DRAFT is offered because the manager's stake
 * reaches a DRAFT vault before the review.
 *
 * A vault not in the catalog at all carries no state, which `?? ''` keeps out: the engine would refuse it too.
 */
export const canSendFaucet = (r: Pick<ApplicationView, 'vaultState'>): boolean =>
  FAUCET_VAULT_STATES.includes(r.vaultState ?? '');

/**
 * The bank's balance as the engine read it from the venue (`balance` on the bank info). `known: false` is a null, a
 * missing field (an older BFF) or a body that is not a decimal string: unknown, never zero. A real "0.000000" is a known
 * zero. The figure is a decimal STRING, carried as it arrived; nothing parses it through a float.
 */
export type FaucetBalance = { known: false } | { known: true; usdc: string };

export const faucetBalance = (info: FaucetBankInfo | null | undefined): FaucetBalance => {
  const balance = info?.balance;
  if (typeof balance !== 'string' || !/^\d+(\.\d+)?$/.test(balance.trim())) return { known: false };
  return { known: true, usdc: balance.trim() };
};

/** A stored request as the drawer lists it: its state, and for a FAILED one the engine's reason. */
export interface FaucetTransferView {
  key: string;
  amount: string;
  createdAt: string | null;
  state: string;
  failed: boolean;
  failureCode: string | null;
  failureReason: string | null;
}

export const FAUCET_FAILED_STATE = 'FAUCET_FAILED';

/** The engine's order (newest first) is kept. A failure shows only on a FAILED row, whatever else the body carries. */
export const faucetTransferViews = (
  rows: FaucetTransferRecord[] | null | undefined
): FaucetTransferView[] =>
  (rows ?? []).map((row, index) => {
    const failed = row.state === FAUCET_FAILED_STATE;
    return {
      key: `${row.createdAt ?? 'unknown'}-${index}`,
      amount: row.amount,
      createdAt: row.createdAt ?? null,
      state: row.state,
      failed,
      failureCode: failed ? (row.failureCode ?? null) : null,
      failureReason: failed ? (row.failureReason ?? null) : null,
    };
  });

/** What the drawer says beside the vault address (f1 review, 2026-10-02). */
export type VaultAddressHint =
  | 'unassigned'
  | 'provisioning'
  | 'sendStakePlusFee'
  | 'sendHere'
  | 'inactive';

/**
 * The vault address's hint by the application's C-08 status. The engine returns `hlAddress` whenever the account
 * exists; this decides what to say about it. ACCOUNT_READY: provisioning, do not send yet — C-08 has the manager
 * transfer from CATALOGUED, once the DRAFT catalog row exists to park the stake against (D-30 cl.2). CATALOGUED /
 * ACTIVATED: the manager sends the stake here. PENDING_REVIEW / OPEN: the address, nothing to add. FAILED / CANCELLED:
 * shown greyed, not accepting transfers — ops still needs it to trace a stuck or refunded transfer, and CANCELLED keeps
 * the account. No address while only REQUESTED: assigned once provisioned; no address later (a failed read, a failed
 * account step): no claim either way.
 *
 * The catalog row decides before the status, as it does for the list's Rejected and Live (rowRejected / rowLive): a
 * rejection closes the vault while C-08 still reads CATALOGUED, so a CLOSED vault is not accepting transfers, and a
 * RAISING / ACTIVE / PAUSED vault is past its stake step, like OPEN.
 */
export const vaultAddressHint = (
  r: Pick<ApplicationView, 'status' | 'hlAddress' | 'vaultState' | 'vaultRaised'>
): { hint: VaultAddressHint | null; muted: boolean } => {
  if (!r.hlAddress) return { hint: r.status === 'REQUESTED' ? 'unassigned' : null, muted: false };
  if (r.vaultState === 'CLOSED') return { hint: 'inactive', muted: true };
  if (rowLive(r)) return { hint: null, muted: false };
  switch (r.status) {
    case 'ACCOUNT_READY':
      return { hint: 'provisioning', muted: false };
    // XBIT-4400: before ACTIVATED the stake's usdSend pays the activation fee out of its amount, so stake + 1.
    case 'CATALOGUED':
      return { hint: 'sendStakePlusFee', muted: false };
    case 'ACTIVATED':
      return { hint: 'sendHere', muted: false };
    case 'FAILED':
    case 'CANCELLED':
      return { hint: 'inactive', muted: true };
    default:
      return { hint: null, muted: false };
  }
};
